import { take } from 'rxjs/operators';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { GenderType, PassengerType } from 'src/app/core/enums/enums';
import { DOCSADoc } from 'src/app/core/models/docsa.model';
import { Passenger, PassengerQuote, PnrInfo } from 'src/app/core/models/itinerary.model';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { Observable } from 'rxjs';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter
} from '@angular/core';

@Component({
  selector: 'app-passenger-docsa-information',
  templateUrl: './passenger-docsa-information.component.html',
  styleUrls: ['./passenger-docsa.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PassengerDocsaInformationComponent implements OnInit {
  @Input() doc: DOCSADoc;
  @Input() passengerDoc: Passenger[];
  @Input() PassengerInformation: PassengerQuote;
  @Input() submitted = false;
  @Input() names: string;
  @Input() id: string;
  @Input() formType: string = 'docsa';

  @Output() docsa = new EventEmitter<DOCSADoc>();

  pnrInfo$: Observable<PnrInfo>;
  isAirline$: Observable<boolean>;

  form: UntypedFormGroup;
  expiredDate = new Date();
  birthday = new Date();
  maxDate = new Date();
  email: string;
  phoneNumber: string;
  minDate = new Date(new Date().getFullYear() - 100, 0, 0);
  firstValueGender = '';
  convertGenderValue = '';
  GENDER_VALUES = [
    { code: GenderType.Female, name: 'genre-female' },
    { code: GenderType.Male, name: 'genre-male' }
  ];
  isDocsRequired: boolean = false;
  activateEmailField: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private itineraryService: ItineraryService,
    private settingsService: SettingsService
  ) {}

  ngOnInit() {
    this.isAirline$ = this.settingsService.getClientIsAirline();
    this.pnrInfo$ = this.itineraryService.getPnrInfo().pipe(take(1));

    if (this.doc) {
      this.completeDocData();
    } else {
      this.completeWithoutDocData();
    }

    this.buildForm();
    this.onValueChange();
    this.birthDaySelectOnPassengerType();
  }

  completeDocData() {
    if (this.doc.pasengerDateOfBirth) {
      this.birthday = new Date(this.doc.pasengerDateOfBirth);
    }
    if (this.doc.expirationDate) {
      this.expiredDate = new Date(this.doc.expirationDate);
    }
    if (this.doc.gender) {
      this.firstValueGender = this.doc.gender;
    }
  }

  completeWithoutDocData() {
    let passengerSelected = this.passengerDoc.find(
      (x) => x.ticket.number === this.PassengerInformation.ticketNumber
    );
    if (passengerSelected) {
      this.convertGenderValue = passengerSelected.gender
        ? passengerSelected.gender == 'M'
          ? GenderType.Male
          : GenderType.Female
        : '';
    }
    this.birthday =
      new Date(passengerSelected.birthDate).toLocaleDateString() === '1/1/1' ||
      passengerSelected.birthDate === null
        ? null
        : new Date(passengerSelected.birthDate);
    (this.email = passengerSelected.email ? passengerSelected.email : ''),
      (this.phoneNumber = passengerSelected.phoneNumber ? passengerSelected.phoneNumber : ''),
      (this.expiredDate = new Date());
    this.firstValueGender = this.convertGenderValue;
  }

  birthDaySelectOnPassengerType() {
    let today = new Date();
    let passenger_type = this.PassengerInformation.passengerType;

    if (passenger_type === PassengerType.Infant) {
      this.maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      this.minDate = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate() + 1);
    }
    if (passenger_type === PassengerType.Child) {
      this.maxDate = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
      this.minDate = new Date(today.getFullYear() - 12, today.getMonth(), today.getDate() + 1);
    }
    if (passenger_type === PassengerType.Adult) {
      this.maxDate = new Date(today.getFullYear() - 12, today.getMonth(), today.getDate());
      this.minDate = new Date(today.getFullYear() - 65, today.getMonth(), today.getDate() + 1);
    }
    if (passenger_type === PassengerType.Senior) {
      this.maxDate = new Date(today.getFullYear() - 65, today.getMonth(), today.getDate());
      this.minDate = new Date(today.getFullYear() - 95, today.getMonth(), today.getDate() + 1);
    }

    !this.birthday &&
      (passenger_type === PassengerType.Adult
        ? (this.birthday = this.maxDate)
        : (this.birthday = this.minDate));
  }

  buildForm() {
    this.form = this.fb.group({
      email: [!!this.email ? this.email : '', [Validators.required, Validators.email]],
      phoneNumber: [!!this.phoneNumber ? this.phoneNumber : '', Validators.required],
      passport: [!!this.doc ? this.doc.passportHolderIndicator : '', Validators.required],
      gender: [this.firstValueGender, Validators.required]
    });

    this.activateEmailField = true;
  }

  onValueChange() {
    if (this.isValid()) {
      this.docsa.emit(this.buildDOCSA());
    }
  }

  buildDOCSA(): DOCSADoc {
    return {
      pasengerDateOfBirth: this.birthday?.toISOString(),
      expirationDate: this.expiredDate?.toISOString(),
      gender: this.form.controls?.gender?.value,
      passportHolderIndicator: 'NA',
      email: this.form.controls?.email?.value,
      phoneNumber: this.form.controls?.phoneNumber?.value
    };
  }

  isValid() {
    return (
      !!this.birthday ||
      this.form.controls?.email?.valid ||
      this.form.controls?.phoneNumber?.valid ||
      this.form.controls?.gender?.valid
    );
  }
}
